import React from "react";

import { Box, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";
import { colors } from "@/theme/overrides/colors";
import { round } from "@/utils/number";

import CalListItem from "./components/CalListItem";

interface Props {
  clientId: string;
}

const useStyles = makeStyles()({
  section: {
    padding: 24,
    backgroundColor: colors.white,
  },
  items: {
    marginTop: 30,
    width: "100%",
    display: "flex",
    alignItems: "flex-start",
  },
  tooltipText: {
    fontWeight: 700,
    color: "#595F68",
  },
});

const calculationsFragment = graphql(`
  fragment CalculationsSection on Client {
    id
    bmi
    basalMetabolicRate
    dailyEnergyExpenditure
  }
`);

const Calculations: React.FC<Props> = ({ clientId }) => {
  const { classes } = useStyles();

  const [client] = useFragment(calculationsFragment, `Client:${clientId}`);

  const bmi = client?.bmi ?? null;
  const bmr = client?.basalMetabolicRate ?? null;
  const dee = client?.dailyEnergyExpenditure ?? null;

  return (
    <>
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={5}
        mb={3}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          Calculations
        </Typography>
        <Box marginY={3} />
      </Box>
      <section className={classes.section}>
        <Typography className={classes.tooltipText} variant="caption">
          All calculations are auto calculated from your gender, current age,
          weight and height and cannot be manually updated.
        </Typography>
        <div className={classes.items}>
          <CalListItem
            icon="person"
            name="Body mass index"
            description="BMI Goal"
            kcal={bmi != null ? round(bmi, 2) : null}
            nokcalLabel={true}
          />
          <CalListItem
            icon="person-bed"
            name="Basal metabolic rate (BMR)"
            description="Client's basal metabolic rate"
            kcal={bmr}
          />
          <CalListItem
            icon="cal-icon"
            name="Daily energy expenditure"
            description="Client's daily energy expenditure"
            kcal={dee}
          />
        </div>
      </section>
    </>
  );
};

export default Calculations;
